import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { setSelectedDeviceId } from '@/store/slices/device/deviceSlice';

interface AnalyticsContextType {
  deviceId: string;
  setDeviceId: (deviceId: string) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const globalDeviceId = useSelector((state: RootState) => state.device.selectedDeviceId);
  const [searchParams] = useSearchParams();
  const urlDeviceId = searchParams.get('deviceId');
  
  // Initialize with URL param if available, otherwise use global state
  useEffect(() => {
    if (urlDeviceId && urlDeviceId !== globalDeviceId) {
      // Update global state with URL param
      dispatch(setSelectedDeviceId(urlDeviceId));
    } else if (globalDeviceId && !urlDeviceId) {
      // Update URL with global state
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('deviceId', globalDeviceId);
      window.history.replaceState(null, '', `?${newSearchParams.toString()}`);
    }
  }, [urlDeviceId, globalDeviceId]);

  // Update URL when deviceId changes
  const setDeviceId = (newDeviceId: string) => {
    dispatch(setSelectedDeviceId(newDeviceId));
  };

  return (
    <AnalyticsContext.Provider value={{ deviceId: globalDeviceId, setDeviceId }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}; 